import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Card, CardContent, Typography, Divider, Paper, Box,
  Alert, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody
} from "@mui/material";
import BienInfoSection from "./BienInfoSection";
import LotRow from "./LotRow";
import { fetchWithAuth } from "utils/fetchWithAuth";
import { formatMonth } from "utils/dateFormat";

export default function FicheBien({ bienId }) {
  const [bien, setBien] = useState(null);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedMonth = searchParams.get("mois") || new Date().toISOString().slice(0, 7);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const API_URL = process.env.REACT_APP_API_URL;
    const query = selectedMonth ? `?mois=${selectedMonth}` : '';
    fetchWithAuth(`${API_URL}/api/biens/${bienId}/${query}`)
      .then(res => res.json())
      .then(bienData => {
        setBien(bienData);
        setLots(Array.isArray(bienData.lots) ? bienData.lots : []);
        setLoading(false);
      })
      .catch((err) => {
        setError("Erreur de chargement");
        setLoading(false);
      });
  }, [bienId, selectedMonth]);

  if (loading) return (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <CircularProgress />
    </Box>
  );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!bien) return null;

  return (
    <Card sx={{ maxWidth: 1000, mx: "auto", mt: 4, mb: 4, p: 0 }}>
      <CardContent>
        <Typography
          variant="h5"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          {bien.est_residence && bien.nom_residence
            ? bien.nom_residence
            : bien.adresse || bien.label || "Bien"
          }
        </Typography>
        <Typography color="text.secondary">{formatMonth(selectedMonth)}</Typography>
        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Informations sur le bien
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3, background: "#f7fafb" }}>
          <BienInfoSection bien={bien} />
        </Paper>

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 3 }}>
          Lots associés à ce bien
        </Typography>
        <Box sx={{ py: 1, px: 2, border: "1px solid #eee", borderRadius: 2, mb: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ mt: 1, minWidth: 600 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f7fafb" }}>
                <TableCell sx={{ fontWeight: 700 }}>Lot</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Locataire</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Entrée</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sortie</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Loyer</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lots.length > 0 ? (
                lots.map(lot => (
                  <LotRow
                    key={lot.id}
                    lot={lot}
                    selectedMonth={selectedMonth}
                    hideActions={true}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Alert severity="info">Aucun lot associé à ce bien</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
}